import AddIcon from '@mui/icons-material/Add';
import { AlertColor, Box, Button, Grid, useMediaQuery, useTheme } from '@mui/material';
import PageLoader from 'components/PageLoader';
import React, { MouseEvent, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import strings from '../../../../../common/Translation/Translate';
import DrawerMenu from '../../../../../components/DrawerMenu';
import Form, { FormDataModel } from '../../../../../components/Form';
import Notification from '../../../../../components/Notification';
import axiosInstance from '../../../../../utils/axios';
import { removeErrorFieldsFromValues } from '../../../../../utils/validators';
import { createDropdownData, getDrawerWidth } from '../../../../../utils/validators/HelperFunctions';
import { KycForm } from './KycForm';
import KycDocumentsTable, { KycDocument } from './KycDocumentsTable';
import { URLS } from 'utils/constants/urls';

type KycFormComponentProps = {
  hasError: any;
  kycDocumentTypes: any;
  handleSave: (e: any) => void;
  formRef: any;
  selectedDocument: KycDocument | null;
};

const KycFormComponent: React.FC<KycFormComponentProps> = ({
  hasError,
  kycDocumentTypes,
  handleSave,
  formRef,
  selectedDocument,
}) => {
  return (
    <>
      <Grid container spacing={2} sx={{ my: 2 }}>
        <Form
          hasError={hasError}
          ref={formRef as RefObject<Form>}
          model={KycForm(strings, kycDocumentTypes, 12)}
          values={selectedDocument}
        />
      </Grid>
      <Grid item xs={12} sm={3} lg={2}>
        <Button fullWidth variant="contained" sx={{ color: '#ffffff' }} onClick={e => handleSave(e)}>
          {strings.common_button_save}
        </Button>
      </Grid>
    </>
  );
};

type KycFormButtonProps = { bookingId: number };

const KycFormButton: React.FC<KycFormButtonProps> = ({ bookingId }) => {
  const theme = useTheme();
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const screenSizeUpLg = useMediaQuery(theme.breakpoints.up('lg'));

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [kycDocumentTypes, setKycDocumentTypes] = useState<any>([]);
  const [documents, setDocuments] = useState<KycDocument[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<KycDocument | null>(null);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const closeNotification = (value: boolean) => setMessage({ ...message, display: value });

  let formRef: RefObject<Form | null | undefined> = useRef();

  const handleCatch = (error: any) => {
    setMessage({
      display: true,
      severity: 'error',
      message: error.response.data?.error_description ?? error.response.data.message ?? error.message,
    });
  };

  const getKycDocumentTypes = useCallback(async () => {
    try {
      setIsLoading(true);
      const { status, data } = await axiosInstance.get(URLS.getKycDocumentTypes);
      if (status === 200) {
        const documentTypes = createDropdownData(data, ['id', 'name']);
        setKycDocumentTypes(documentTypes);
      }
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    getKycDocumentTypes();
  }, [getKycDocumentTypes]);

  const fetchKycDocuments = async () => {
    try {
      setIsLoading(true);
      const { status, data } = await axiosInstance.get(URLS.getKycDocuments(bookingId));
      if (status === 200) {
        setDocuments(data || []);
      }
    } catch (error) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchKycDocuments();
  }, []);

  const handleClose = () => {
    setOpenDrawer(false);
    setHasError(false);
    setSelectedDocument(null);
    formRef?.current?.resetForm();
  };

  const handleSaveHelper = async (data: any) => {
    setIsLoading(true);
    try {
      const method = isEditMode && selectedDocument ? 'put' : 'post';
      const url =
        isEditMode && selectedDocument
          ? URLS.updateKycDocument(selectedDocument.bookingId, selectedDocument.id)
          : URLS.uploadUserDocuments(bookingId);

      const { status } = await axiosInstance[method](url, data);

      if (status === 200) {
        setMessage({
          display: true,
          severity: 'success',
          message: strings.common_save_message,
        });
        await fetchKycDocuments();
        handleClose();
      } else {
        setMessage({
          display: true,
          severity: 'error',
          message: strings.something_went_wrong,
        });
      }
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const { getFormData } = formRef.current as {
      getFormData: () => { formData: FormDataModel; isFormValid: boolean };
    };
    const { formData, isFormValid } = getFormData();
    const data = removeErrorFieldsFromValues(formData) as any;
    setHasError(!isFormValid);

    if (isFormValid) handleSaveHelper(data);
  };

  const handleDocumentDeleted = async (index: number) => {
    const document = documents[index];
    if (!document) return;

    try {
      setIsLoading(true);
      const { status } = await axiosInstance.delete(URLS.deleteKycDocument(bookingId, document.id));
      if (status === 204) {
        await fetchKycDocuments();
        setMessage({
          display: true,
          severity: 'success',
          message: 'Document deleted successfully.',
        });
      }
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (index: number) => {
    const doc = documents[index];
    if (!doc) return;

    setSelectedDocument(doc);
    setIsEditMode(true);
    setOpenDrawer(true);
  };

  return (
    <>
      {isLoading && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}
      <Button
        onClick={() => {
          setSelectedDocument(null);
          setOpenDrawer(true);
        }}
        disabled={isLoading}
        variant="outlined"
        color={'primary'}
        startIcon={<AddIcon />}
        sx={{ minHeight: '36px', my: 0 }}>
        {strings.kyc_form_title}
      </Button>
      {documents.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <KycDocumentsTable handleEdit={handleEdit} handleDelete={handleDocumentDeleted} documents={documents} />
        </Box>
      )}
      <DrawerMenu
        width={getDrawerWidth(screenSizeDownSm, screenSizeDownMd, screenSizeUpLg)}
        open={openDrawer}
        handleClose={handleClose}
        closeButton={true}
        title={strings.kycc_button_title}>
        {
          <KycFormComponent
            hasError={hasError}
            selectedDocument={selectedDocument}
            kycDocumentTypes={kycDocumentTypes}
            handleSave={handleSave}
            formRef={formRef}
          />
        }
      </DrawerMenu>
    </>
  );
};

export { KycFormButton, KycDocumentsTable };
